<template lang="pug">
    el-drawer.customer-group-form-drawer(
        :title="title"
        :visible.sync="visible"
        :before-close="confirmDiscard(() => changedSince(formSnapshot, form))"
        :wrapper-closable="false"
        :modal="true"
        :append-to-body="true"
        size="480px"
        @closed="handleClosed"
    )
        .drawer-body
            el-form(
                ref="form"
                :model="form"
                :rules="rules"
                :validate-on-rule-change="false"
                label-position="top"
                @submit.native.prevent="submit"
            )
                el-form-item(:label="$t('Key')" prop="key")
                    el-input(
                        v-model="form.key"
                        :placeholder="$t('e.g. b2b, wholesale')"
                        :maxlength="64"
                    )
                el-form-item(:label="$t('Name')" prop="name")
                    el-input(v-model="form.name" :maxlength="120")
                el-form-item(:label="$t('Description')" prop="description")
                    el-input(
                        v-model="form.description"
                        type="textarea"
                        :autosize="{ minRows: 2, maxRows: 5 }"
                    )
                .flags-row
                    el-form-item.flag-item(prop="default")
                        el-switch(
                            :active-text="$t('Default')"
                            v-model="form.default"
                            :disabled="initialDefault"
                        )
                    el-form-item.flag-item(
                        v-if="alternativeGuestCustomerGroupsFeatureEnabled"
                        prop="guest_eligible"
                    )
                        el-switch(
                            :active-text="$t('Guest eligible')"
                            v-model="form.guest_eligible"
                        )
                .el-form-item__hint(v-if="initialDefault") {{ $t('A shop must always have one default group. To change the default, set another group to default.') }}
                .el-form-item__hint(v-if="alternativeGuestCustomerGroupsFeatureEnabled && form.guest_eligible") {{ $t('Anonymous visitors can be assigned to this group. Products without a price for this group are invisible to those visitors.') }}
                el-form-item.with-top-spacing(:label="$t('Currency')" prop="currency.id" required)
                    .currency-row
                        el-select(
                            v-model="form.currency.id"
                            filterable
                            :loading="currencyOptionsLoading"
                            :placeholder="$t('Select a currency')"
                            :no-data-text="$t('No currencies yet — create one')"
                            :no-match-text="$t('No currencies found')"
                            class="currency-select"
                        )
                            el-option(
                                v-for="option in currencyOptions"
                                :key="option.id"
                                :label="`${option.iso4217} ${option.sign ? '(' + option.sign + ')' : ''}`"
                                :value="option.id"
                            )
                        el-button(
                            plain
                            icon="el-icon-fa-plus fas"
                            :title="$t('Quick-create currency')"
                            @click="openCurrencyQuickCreate"
                        ) {{ $t('New') }}
        .drawer-footer
            .footer-left
                el-button(
                    round
                    size="mini"
                    icon="el-icon-fa-times fas"
                    :disabled="saving"
                    @click="closeDrawer"
                ) {{ $t('Dismiss') }}
            .footer-right
                el-button(
                    v-if="form.id"
                    round
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt fas"
                    :disabled="saving || initialDefault"
                    :title="initialDefault ? $t('The default group cannot be deleted') : ''"
                    @click="$emit('delete', form.id)"
                ) {{ $t('Delete') }}
                el-button(
                    round
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="saving"
                    @click="submit"
                ) {{ $t('Save') }}

        currency-form(
            :visibility.sync="quickCreate.visibility"
            :data="{}"
            :saving="quickCreate.saving"
            :existing-iso-codes="existingIsoCodes"
            :is-quick-create="true"
            @submit="handleQuickCreateSubmit"
            @closed="quickCreate.saving = false"
        )
</template>

<i18n>
{
    "de": {
        "Key": "Schlüssel",
        "Name": "Name",
        "Description": "Beschreibung",
        "Default": "Standard",
        "Guest eligible": "Für Gäste verwendbar",
        "Anonymous visitors can be assigned to this group. Products without a price for this group are invisible to those visitors.": "Anonyme Besucher können dieser Gruppe zugeordnet werden. Produkte ohne Preis für diese Gruppe sind für diese Besucher unsichtbar.",
        "Currency": "Währung",
        "Select a currency": "Währung auswählen",
        "No currencies yet — create one": "Noch keine Währungen — erstelle eine",
        "No currencies found": "Keine Währungen gefunden",
        "New": "Neu",
        "Quick-create currency": "Währung schnell anlegen",
        "New Customer Group": "Neue Kundengruppe",
        "Edit Customer Group": "Kundengruppe bearbeiten",
        "Save": "Speichern",
        "Dismiss": "Abbrechen",
        "Delete": "Löschen",
        "e.g. b2b, wholesale": "z.B. b2b, wholesale",
        "A shop must always have one default group. To change the default, set another group to default.": "Ein Shop muss immer eine Standard-Gruppe haben. Um den Standard zu ändern, setze eine andere Gruppe als Standard.",
        "The default group cannot be deleted": "Die Standardgruppe kann nicht gelöscht werden",
        "Currency saved": "Währung gespeichert",
        "Currency could not be saved": "Währung konnte nicht gespeichert werden"
    }
}
</i18n>

<script>
import _ from 'lodash';
import pinia from '@/pinia';
import { useSetupStore, useShopCurrencyStore } from '@/stores';

import CurrencyForm from '@/extensions/shop/views/Currency/Form';
import discardGuard from '@/mixins/discardGuard';

export default {
    name: 'CustomerGroupForm',
    mixins: [discardGuard],
    components: { CurrencyForm },
    props: {
        visibility: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: () => ({}),
        },
        saving: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: this.buildForm(this.data),
            formSnapshot: undefined,
            initialDefault: !!_.get(this.data, 'default'),
            currencyOptionsLoading: false,
            quickCreate: {
                visibility: false,
                saving: false,
            },
            rules: {
                key: 'required|key',
                name: 'required',
                currency: {
                    id: 'required',
                },
            },
        };
    },
    computed: {
        visible: {
            get() {
                return this.visibility;
            },
            set(value) {
                this.$emit('update:visibility', value);
            },
        },
        title() {
            return this.form.id ? this.$t('Edit Customer Group') : this.$t('New Customer Group');
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        alternativeGuestCustomerGroupsFeatureEnabled() {
            if (! (this.setupStore.enabledFeatures || []).includes('alternative-guest-customer-groups')) return false;

            // Feature is opt-in per CMS via the picker-mode setting. `none`
            // (default) means the admin hasn't activated guest market handling
            // for this shop — hide the toggle.
            // Note: admin-ui forbids optional chaining (?.) — use _.get instead.
            const pickerMode = _.get(this.setupStore, 'settingValues["guest-market-picker-mode"]');
            return pickerMode && pickerMode !== 'none';
        },
        currencyStore() {
            return useShopCurrencyStore(pinia);
        },
        activeShop() {
            const cms = this.setupStore.activeCms;
            return cms ? cms.shop : null;
        },
        currencyOptions() {
            return this.currencyStore.availableCurrencies || [];
        },
        existingIsoCodes() {
            return _.map(this.currencyOptions, 'iso4217');
        },
    },
    watch: {
        data: {
            handler(value) {
                this.form = this.buildForm(value);
                this.initialDefault = !!_.get(value, 'default');
            },
            deep: true,
        },
        visibility(value) {
            if (value) {
                this.form = this.buildForm(this.data);
                this.formSnapshot = this.snapshotOf(this.form);
                this.initialDefault = !!_.get(this.data, 'default');
                this.loadCurrencyOptions();
            }
        },
    },
    mounted() {
        if (this.visibility) this.loadCurrencyOptions();
    },
    methods: {
        buildForm(source) {
            const currency = _.get(source, 'currency') || {};
            return {
                id: source && source.id ? source.id : null,
                key: source && source.key ? source.key : '',
                name: source && source.name ? source.name : '',
                description: source && source.description ? source.description : '',
                default: source && typeof source.default === 'boolean' ? source.default : false,
                guest_eligible: source && typeof source.guest_eligible === 'boolean' ? source.guest_eligible : false,
                currency: { id: currency.id || null },
            };
        },
        loadCurrencyOptions() {
            const cmsId = _.get(this.setupStore.activeCms, 'id');
            // Clear before the fetch so the dropdown can't show currencies
            // left over from a previous CMS while the network request is in
            // flight. If the user picks one of those stale options the form
            // submits a currency_id that doesn't belong to the active shop
            // and the mutation 404s on Currency lookup.
            this.currencyStore.setAvailableCurrencies([]);
            this.currencyOptionsLoading = true;
            return this.currencyStore.fetchAvailableCurrencies(cmsId)
                .catch((err) => { console.error(err); })
                .finally(() => { this.currencyOptionsLoading = false; });
        },
        submit() {
            this.$refs.form.validate((valid) => {
                if (!valid) return;
                const payload = _.cloneDeep(this.form);
                if (this.activeShop) {
                    payload.shop = { id: this.activeShop.id };
                }
                this.$emit('submit', payload);
            });
        },
        closeDrawer() {
            this.visible = false;
        },
        handleClosed() {
            if (this.$refs.form) this.$refs.form.clearValidate();
            this.form = this.buildForm({});
            this.initialDefault = false;
            this.quickCreate.visibility = false;
            this.quickCreate.saving = false;
            this.$emit('closed');
        },
        openCurrencyQuickCreate() {
            this.quickCreate.visibility = true;
        },
        async handleQuickCreateSubmit(payload) {
            this.quickCreate.saving = true;
            try {
                const created = await this.currencyStore.save(payload, this.activeShop ? this.activeShop.id : null);
                await this.loadCurrencyOptions();
                this.form.currency = { id: created.id };
                this.quickCreate.visibility = false;
                this.$notify.success({
                    title: this.$t('Currency saved'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                });
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('Currency could not be saved'),
                    message: _.get(err, 'graphQLErrors.0.message') || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.quickCreate.saving = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.customer-group-form-drawer
    ::v-deep .el-drawer
        display: flex
        flex-direction: column
        .drawer-body
            flex-grow: 1
            padding: 1rem
            overflow: auto
        .currency-row
            display: flex
            gap: 0.5rem
            align-items: center
            .currency-select
                flex-grow: 1
        .flags-row
            display: flex
            flex-wrap: wrap
            gap: 1.5rem
            align-items: center
            margin-bottom: 0
            .flag-item
                margin-bottom: 0
        .with-top-spacing
            margin-top: 18px
        .el-form-item__hint
            display: block
            margin-top: 0.25rem
            font-size: 0.8rem
            line-height: 1.4
            color: #909399
        .drawer-footer
            display: flex
            justify-content: space-between
            align-items: center
            padding: 12px 20px
            border-top: 1px solid #e4e7ed
            .footer-left,
            .footer-right
                display: flex
                gap: 8px
</style>
